<template>
  <div class="boolean-search-layout">
    <section>
      <istarshine-form
          v-if="!isBusy"
          :model="model"
          :title="$t('istarshine.config_title')"
          :errorMessage="errorMessage"
          :isEditMode="isEditMode"
          :savingRow="savingRow"
          :deletingRow="deletingRow"
          :availableProviders="availableProviders"
          :validationByField="validationByField"
          :queryValidityIsBusy="queryValidityIsBusy"
          :validateQueryForField="validateQueryForField"
          :configIds="configIds"
          :deletingCommon="deletingCommon"
          :deletingProvider="deletingProvider"
          :validOperators="validOperators"
          @saveCommonQuery="saveCommonQuery"
          @saveProviderQuery="saveProviderQuery"
          @deleteSearch="deleteSearch"
          @queryTypeChanged="onQueryTypeChanged"
          @toggleEdit="toggleEdit"
          @cancel="cancelEdit"
          @addCommonQuery="addCommonQuery"
          @removeCommonQuery="removeCommonQuery"
          @updateCommonQuery="updateCommonQuery"
          @addProviderQuery="addProviderQuery"
          @removeProviderQuery="removeProviderQuery"
          @updateProviderQuery="updateProviderQuery"
          @updateCommonExcludedUsers="updateCommonExcludedUsers"
          @updateProviderExcludedUsers="updateProviderExcludedUsers"
          @updateCommonIncludedUsers="updateCommonIncludedUsers"
          @updateProviderIncludedUsers="updateProviderIncludedUsers"
          @updateCommonUserFilterMode="updateCommonUserFilterMode"
          @updateProviderUserFilterMode="updateProviderUserFilterMode"
          @updateCommonFollowerFilter="updateCommonFollowerFilter"
          @updateProviderFollowerFilter="updateProviderFollowerFilter"
      />

      <div v-else class="text-center">
        <loader-inline />
      </div>
    </section>
  </div>
</template>

<script>
// Plugins
import { mapState, mapActions } from 'vuex';
import axios from 'axios';

// Components
import IstarshineForm from "../../components/istarshine/IstarshineForm.vue";
import loaderInline from '../../components/loader-inline.vue'

// Complete list of all possible providers - used only for reference/validation
// UI should use availableProviders computed property from project settings
const VALID_PROVIDERS = ['wechat_mention', 'douyin_mention', 'red_mention', 'weibo_mention', 'toutiao_mention'];
const CONFIG_PATH = 'searches/asian_socials_config';
const EXCLUDED_USERS_MAX = 50;
const INCLUDED_USERS_MAX = 50;
const VALID_OPERATORS = ['<', '<=', '>', '>=', '><'];

function normalizeExcludedUsers(raw) {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map(s => String(s).trim()).filter(Boolean).slice(0, EXCLUDED_USERS_MAX);
}

function normalizeIncludedUsers(raw) {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map(s => String(s).trim()).filter(Boolean).slice(0, INCLUDED_USERS_MAX);
}

function emptyExcludedPerProvider(availableProviders) {
  const o = {};
  availableProviders.forEach(p => { o[p] = [[]]; });
  return o;
}

function emptyIncludedPerProvider(availableProviders) {
  const o = {};
  availableProviders.forEach(p => { o[p] = [[]]; });
  return o;
}

function emptyFollowerFilterPerProvider(availableProviders) {
  const o = {};
  availableProviders.forEach(p => { o[p] = [{ min: null, max: null, operator: null }]; });
  return o;
}

function emptyConfigIds(availableProviders) {
  return {
    common: [],
    ...availableProviders.reduce((acc, p) => { acc[p] = []; return acc; }, {})
  };
}

function cloneConfigIds(ids) {
  if (!ids || typeof ids !== 'object') return { common: [] };
  const result = {};
  for (const [key, val] of Object.entries(ids)) {
    result[key] = Array.isArray(val) ? [...val] : val;
  }
  return result;
}

function cloneIstarshineModel(m) {
  if (!m || typeof m !== 'object') return {};
  const clonedPerProvider = {};
  if (m.boolean_queries_per_provider) {
    for (const [key, val] of Object.entries(m.boolean_queries_per_provider)) {
      clonedPerProvider[key] = Array.isArray(val) ? [...val] : val;
    }
  }
  const excludedCommon = Array.isArray(m.excluded_users_common)
    ? m.excluded_users_common.map(row => Array.isArray(row) ? [...row] : [])
    : [[]];
  const excludedPerProvider = {};
  if (m.excluded_users_per_provider) {
    for (const [key, val] of Object.entries(m.excluded_users_per_provider)) {
      excludedPerProvider[key] = Array.isArray(val)
        ? val.map(row => (Array.isArray(row) ? [...row] : []))
        : [[]];
    }
  }
  const includedCommon = Array.isArray(m.included_users_common)
    ? m.included_users_common.map(row => Array.isArray(row) ? [...row] : [])
    : [[]];
  const includedPerProvider = {};
  if (m.included_users_per_provider) {
    for (const [key, val] of Object.entries(m.included_users_per_provider)) {
      includedPerProvider[key] = Array.isArray(val)
        ? val.map(row => (Array.isArray(row) ? [...row] : []))
        : [[]];
    }
  }
  const followerFilterCommon = Array.isArray(m.follower_filter_common)
    ? m.follower_filter_common.map(f => ({ ...f }))
    : [{ min: null, max: null, operator: null }];
  const followerFilterPerProvider = {};
  if (m.follower_filter_per_provider) {
    for (const [key, val] of Object.entries(m.follower_filter_per_provider)) {
      followerFilterPerProvider[key] = Array.isArray(val)
        ? val.map(f => ({ ...f }))
        : [{ min: null, max: null, operator: null }];
    }
  }
  const userFilterModeCommon = Array.isArray(m.user_filter_mode_common)
    ? [...m.user_filter_mode_common]
    : ['exclude'];
  const userFilterModePerProvider = {};
  if (m.user_filter_mode_per_provider) {
    for (const [key, val] of Object.entries(m.user_filter_mode_per_provider)) {
      userFilterModePerProvider[key] = Array.isArray(val) ? [...val] : ['exclude'];
    }
  }
  return {
    query_type: m.query_type,
    common_boolean_queries: Array.isArray(m.common_boolean_queries)
      ? [...m.common_boolean_queries]
      : (m.common_boolean_queries || ['']),
    boolean_queries_per_provider: clonedPerProvider,
    excluded_users_common: excludedCommon,
    excluded_users_per_provider: excludedPerProvider,
    included_users_common: includedCommon,
    included_users_per_provider: includedPerProvider,
    follower_filter_common: followerFilterCommon,
    follower_filter_per_provider: followerFilterPerProvider,
    user_filter_mode_common: userFilterModeCommon,
    user_filter_mode_per_provider: userFilterModePerProvider,
    hasConfig: m.hasConfig
  };
}

export default {
  name: "istarshine-view",

  components: {
    IstarshineForm,
    loaderInline
  },

  props: {
    projectSettings: { type: Object, default: null }
  },

  data() {
    return {
      model: {
        query_type: 'all',
        common_boolean_queries: [''],
        boolean_queries_per_provider: {},
        excluded_users_common: [[]],
        excluded_users_per_provider: {},
        included_users_common: [[]],
        included_users_per_provider: {},
        follower_filter_common: [{ min: null, max: null, operator: null }],
        follower_filter_per_provider: {},
        user_filter_mode_common: ['exclude'],
        user_filter_mode_per_provider: {},
        hasConfig: false
      },
      originalModel: {},
      configIds: { common: [] },
      originalConfigIds: { common: [] },
      isBusy: true,
      isEditMode: false,
      errorMessage: '',
      savingRow: null,
      deletingRow: null,
      deletingCommon: false,
      deletingProvider: null,
      validationByField: {},
      validOperators: [...VALID_OPERATORS]
    }
  },

  computed: {
    ...mapState({
      project_id: state => state.booleanSearch.project_id,
      client_id: state => state.booleanSearch.client_id,
      query_validity: state => state.booleanSearch.query_validity
    }),
    queryValidityIsBusy() {
      return this.query_validity && this.query_validity.isBusy;
    },
    availableProviders() {
      if (!this.projectSettings?.enabled_asian_social_providers) {
        return [];
      }
      return this.projectSettings.enabled_asian_social_providers;
    }
  },

  created() {
    this.initializeProviderDataStructures();
    this.fetchConfig();
  },

  watch: {
    projectSettings: {
      handler(newProjectSettings, oldProjectSettings) {
        if (!oldProjectSettings || newProjectSettings?.id !== oldProjectSettings?.id) {
          this.initializeProviderDataStructures();
          this.fetchConfig();
        }
      },
    }
  },

  methods: {
    ...mapActions({
      validateQuery: 'booleanSearch/validateQuery'
    }),

    initializeProviderDataStructures() {
      if (!this.availableProviders || this.availableProviders.length === 0) {
        return;
      }
      
      if (this.model.hasConfig || !this.isBusy) {
        return;
      }
      
      const emptyQueries = {};
      this.availableProviders.forEach(p => { emptyQueries[p] = ['']; });
      this.$set(this.model, 'boolean_queries_per_provider', { ...emptyQueries });
      this.$set(this.model, 'excluded_users_per_provider', emptyExcludedPerProvider(this.availableProviders));
      this.$set(this.model, 'included_users_per_provider', emptyIncludedPerProvider(this.availableProviders));
      this.$set(this.model, 'follower_filter_per_provider', emptyFollowerFilterPerProvider(this.availableProviders));
      
      const configIds = emptyConfigIds(this.availableProviders);
      this.configIds = configIds;
      this.originalConfigIds = { ...configIds };
    },

    syncHasConfigFromConfigIds() {
      const hasAny =
        this.configIds.common.some(id => id !== null && id !== undefined) ||
        this.availableProviders.some(p => (this.configIds[p] || []).some(id => id !== null && id !== undefined));
      this.$set(this.model, 'hasConfig', hasAny);
    },

    snapshotState() {
      this.originalModel = cloneIstarshineModel(this.model);
      this.originalConfigIds = cloneConfigIds(this.configIds);
    },

    validateQueryForField(fieldKey, queryString) {
      const query = (queryString && String(queryString).trim()) || '';
      if (!query) {
        this.$set(this.validationByField, fieldKey, { isValid: true, errorMessage: '' });
        return Promise.resolve();
      }
      return this.validateQuery({
        type: 'regex_query',
        model: { regex_query: query },
        isTagSearch: false
      })
        .then(() => {
          const v = this.query_validity;
          this.$set(this.validationByField, fieldKey, {
            isValid: v.isValid,
            errorMessage: v.errorMessage || ''
          });
        })
        .catch(() => {
          this.$set(this.validationByField, fieldKey, {
            isValid: false,
            errorMessage: this.$t('booleanSearch.query_validation.generic_error')
          });
        });
    },

    // ── Add / Update helpers (no backend calls) ──

    addCommonQuery() {
      this.model.common_boolean_queries.push('');
      this.configIds.common.push(null);
      if (!Array.isArray(this.model.excluded_users_common)) {
        this.$set(this.model, 'excluded_users_common', []);
      }
      this.model.excluded_users_common.push([]);
      if (!Array.isArray(this.model.included_users_common)) {
        this.$set(this.model, 'included_users_common', []);
      }
      this.model.included_users_common.push([]);
      if (!Array.isArray(this.model.follower_filter_common)) {
        this.$set(this.model, 'follower_filter_common', []);
      }
      this.model.follower_filter_common.push({ min: null, max: null, operator: null });
      if (!Array.isArray(this.model.user_filter_mode_common)) {
        this.$set(this.model, 'user_filter_mode_common', []);
      }
      this.model.user_filter_mode_common.push('exclude');
    },

    updateCommonQuery(index, value) {
      this.$set(this.model.common_boolean_queries, index, value);
    },

    addProviderQuery(provider) {
      if (!Array.isArray(this.model.boolean_queries_per_provider[provider])) {
        this.$set(this.model.boolean_queries_per_provider, provider, ['']);
      } else {
        this.model.boolean_queries_per_provider[provider].push('');
      }
      if (!Array.isArray(this.configIds[provider])) {
        this.$set(this.configIds, provider, [null]);
      } else {
        this.configIds[provider].push(null);
      }
      if (!this.model.excluded_users_per_provider) {
        this.$set(this.model, 'excluded_users_per_provider', emptyExcludedPerProvider(this.availableProviders));
      }
      if (!Array.isArray(this.model.excluded_users_per_provider[provider])) {
        this.$set(this.model.excluded_users_per_provider, provider, []);
      }
      this.model.excluded_users_per_provider[provider].push([]);
      if (!this.model.included_users_per_provider) {
        this.$set(this.model, 'included_users_per_provider', emptyIncludedPerProvider(this.availableProviders));
      }
      if (!Array.isArray(this.model.included_users_per_provider[provider])) {
        this.$set(this.model.included_users_per_provider, provider, []);
      }
      this.model.included_users_per_provider[provider].push([]);
      if (!this.model.follower_filter_per_provider) {
        this.$set(this.model, 'follower_filter_per_provider', emptyFollowerFilterPerProvider(this.availableProviders));
      }
      if (!Array.isArray(this.model.follower_filter_per_provider[provider])) {
        this.$set(this.model.follower_filter_per_provider, provider, []);
      }
      this.model.follower_filter_per_provider[provider].push({ min: null, max: null, operator: null });
      if (!this.model.user_filter_mode_per_provider) {
        const modes = {};
        this.availableProviders.forEach(p => { modes[p] = []; });
        this.$set(this.model, 'user_filter_mode_per_provider', modes);
      }
      if (!Array.isArray(this.model.user_filter_mode_per_provider[provider])) {
        this.$set(this.model.user_filter_mode_per_provider, provider, []);
      }
      this.model.user_filter_mode_per_provider[provider].push('exclude');
    },

    updateProviderQuery(provider, index, value) {
      this.$set(this.model.boolean_queries_per_provider[provider], index, value);
    },

    updateCommonExcludedUsers(index, value) {
      const urls = normalizeExcludedUsers(value);
      if (!Array.isArray(this.model.excluded_users_common)) {
        this.$set(this.model, 'excluded_users_common', []);
      }
      while (this.model.excluded_users_common.length <= index) {
        this.model.excluded_users_common.push([]);
      }
      this.$set(this.model.excluded_users_common, index, urls);
    },

    updateProviderExcludedUsers(provider, index, value) {
      const urls = normalizeExcludedUsers(value);
      if (!this.model.excluded_users_per_provider) {
        this.$set(this.model, 'excluded_users_per_provider', emptyExcludedPerProvider(this.availableProviders));
      }
      if (!Array.isArray(this.model.excluded_users_per_provider[provider])) {
        this.$set(this.model.excluded_users_per_provider, provider, []);
      }
      const rows = this.model.excluded_users_per_provider[provider];
      while (rows.length <= index) {
        rows.push([]);
      }
      this.$set(rows, index, urls);
    },

    updateCommonIncludedUsers(index, value) {
      const urls = normalizeIncludedUsers(value);
      if (!Array.isArray(this.model.included_users_common)) {
        this.$set(this.model, 'included_users_common', []);
      }
      while (this.model.included_users_common.length <= index) {
        this.model.included_users_common.push([]);
      }
      this.$set(this.model.included_users_common, index, urls);
    },

    updateProviderIncludedUsers(provider, index, value) {
      const urls = normalizeIncludedUsers(value);
      if (!this.model.included_users_per_provider) {
        this.$set(this.model, 'included_users_per_provider', emptyIncludedPerProvider(this.availableProviders));
      }
      if (!Array.isArray(this.model.included_users_per_provider[provider])) {
        this.$set(this.model.included_users_per_provider, provider, []);
      }
      const rows = this.model.included_users_per_provider[provider];
      while (rows.length <= index) {
        rows.push([]);
      }
      this.$set(rows, index, urls);
    },

    updateCommonUserFilterMode(index, mode) {
      if (!Array.isArray(this.model.user_filter_mode_common)) {
        this.$set(this.model, 'user_filter_mode_common', []);
      }
      while (this.model.user_filter_mode_common.length <= index) {
        this.model.user_filter_mode_common.push('exclude');
      }
      this.$set(this.model.user_filter_mode_common, index, mode);
      if (mode === 'exclude') {
        this.$set(this.model.included_users_common, index, []);
      } else {
        this.$set(this.model.excluded_users_common, index, []);
      }
    },

    updateProviderUserFilterMode(provider, index, mode) {
      if (!this.model.user_filter_mode_per_provider) {
        const modes = {};
        this.availableProviders.forEach(p => { modes[p] = []; });
        this.$set(this.model, 'user_filter_mode_per_provider', modes);
      }
      if (!Array.isArray(this.model.user_filter_mode_per_provider[provider])) {
        this.$set(this.model.user_filter_mode_per_provider, provider, []);
      }
      while (this.model.user_filter_mode_per_provider[provider].length <= index) {
        this.model.user_filter_mode_per_provider[provider].push('exclude');
      }
      this.$set(this.model.user_filter_mode_per_provider[provider], index, mode);
      if (mode === 'exclude') {
        this.$set(this.model.included_users_per_provider[provider], index, []);
      } else {
        this.$set(this.model.excluded_users_per_provider[provider], index, []);
      }
    },

    updateCommonFollowerFilter(index, filter) {
      if (!Array.isArray(this.model.follower_filter_common)) {
        this.$set(this.model, 'follower_filter_common', []);
      }
      while (this.model.follower_filter_common.length <= index) {
        this.model.follower_filter_common.push({ min: null, max: null, operator: null });
      }
      this.$set(this.model.follower_filter_common, index, { ...filter });
    },

    updateProviderFollowerFilter(provider, index, filter) {
      if (!this.model.follower_filter_per_provider) {
        this.$set(this.model, 'follower_filter_per_provider', emptyFollowerFilterPerProvider(this.availableProviders));
      }
      if (!Array.isArray(this.model.follower_filter_per_provider[provider])) {
        this.$set(this.model.follower_filter_per_provider, provider, []);
      }
      while (this.model.follower_filter_per_provider[provider].length <= index) {
        this.model.follower_filter_per_provider[provider].push({ min: null, max: null, operator: null });
      }
      this.$set(this.model.follower_filter_per_provider[provider], index, { ...filter });
    },

    // ── Per-row Save (POST or PUT) ──

    async saveCommonQuery(index) {
      const query = (this.model.common_boolean_queries[index] || '').trim();
      if (!query) {
        this.errorMessage = this.$t('istarshine.validation.query_required');
        return;
      }
      if (query.length > 100) {
        this.errorMessage = this.$t('istarshine.validation.query_max_length');
        return;
      }
      await this.validateQueryForField('common_' + index, query);
      const fieldValidation = this.validationByField['common_' + index];
      if (fieldValidation && fieldValidation.isValid === false) {
        this.errorMessage = fieldValidation.errorMessage || this.$t('booleanSearch.query_validation.generic_error');
        return;
      }

      const rowKey = 'common_' + index;
      this.savingRow = rowKey;
      this.errorMessage = '';

      try {
        const baseUrl = `projects/${this.project_id}/${CONFIG_PATH}`;
        const id = this.configIds.common[index];
        const userFilterMode = this.model.user_filter_mode_common && this.model.user_filter_mode_common[index] || 'exclude';
        const excluded_users = userFilterMode === 'exclude' ? normalizeExcludedUsers(
          this.model.excluded_users_common && this.model.excluded_users_common[index]
        ) : [];
        const included_users = userFilterMode === 'include' ? normalizeIncludedUsers(
          this.model.included_users_common && this.model.included_users_common[index]
        ) : [];
        const followerFilter = this.model.follower_filter_common && this.model.follower_filter_common[index] || {};
        const follower_count_min = followerFilter.min;
        const follower_count_max = followerFilter.max;
        const follower_count_operator = followerFilter.operator;
        let row;

        const payload = { 
          query, 
          excluded_users,
          included_users,
          follower_count_min,
          follower_count_max,
          follower_count_operator
        };

        if (id !== null && id !== undefined) {
          const { data: res } = await axios.put(`${baseUrl}/${id}`, payload);
          row = res.data || res;
        } else {
          // Before creating a common row, clean up any conflicting per-provider rows
          for (const p of this.availableProviders) {
            const pIds = (this.configIds[p] || []).filter(i => i !== null && i !== undefined);
            if (pIds.length > 0) {
              await this.deleteConfigIds(baseUrl, pIds);
              this.$set(this.configIds, p, []);
            }
          }
          payload.provider = null;
          const { data: res } = await axios.post(baseUrl, payload);
          row = res.data || res;
        }

        this.$set(this.configIds.common, index, row.id);
        this.$set(this.model.common_boolean_queries, index, row.query || query);
        if (Array.isArray(row.excluded_users)) {
          this.$set(this.model.excluded_users_common, index, normalizeExcludedUsers(row.excluded_users));
        } else {
          this.$set(this.model.excluded_users_common, index, excluded_users);
        }
        if (Array.isArray(row.included_users)) {
          this.$set(this.model.included_users_common, index, normalizeIncludedUsers(row.included_users));
        } else {
          this.$set(this.model.included_users_common, index, included_users);
        }
        this.$set(this.model.follower_filter_common, index, {
          min: row.follower_count_min,
          max: row.follower_count_max,
          operator: row.follower_count_operator
        });
        const hasIncluded = included_users.length > 0;
        
        // Ensure user_filter_mode_common exists as an array
        if (!Array.isArray(this.model.user_filter_mode_common)) {
          this.$set(this.model, 'user_filter_mode_common', []);
        }
        // Ensure the array is long enough
        while (this.model.user_filter_mode_common.length <= index) {
          this.model.user_filter_mode_common.push('exclude');
        }
        
        this.$set(this.model.user_filter_mode_common, index, hasIncluded ? 'include' : 'exclude');
        this.syncHasConfigFromConfigIds();
        this.snapshotState();
        this.notifySuccess(this.$t('istarshine.save_success'));
      } catch (error) {
        console.error('Error saving common query:', error);
        this.errorMessage = error.response?.data?.message || this.$t('istarshine.save_error');
      } finally {
        this.savingRow = null;
      }
    },

    async saveProviderQuery(provider, index) {
      const arr = this.model.boolean_queries_per_provider && this.model.boolean_queries_per_provider[provider];
      const query = (arr && arr[index] || '').trim();
      if (!query) {
        this.errorMessage = this.$t('istarshine.validation.query_required');
        return;
      }
      if (query.length > 100) {
        this.errorMessage = this.$t('istarshine.validation.query_max_length');
        return;
      }
      await this.validateQueryForField(provider + '_' + index, query);
      const fieldValidation = this.validationByField[provider + '_' + index];
      if (fieldValidation && fieldValidation.isValid === false) {
        this.errorMessage = fieldValidation.errorMessage || this.$t('booleanSearch.query_validation.generic_error');
        return;
      }

      const rowKey = provider + '_' + index;
      this.savingRow = rowKey;
      this.errorMessage = '';

      try {
        const baseUrl = `projects/${this.project_id}/${CONFIG_PATH}`;
        const ids = this.configIds[provider] || [];
        const id = ids[index];
        const userFilterMode = this.model.user_filter_mode_per_provider && this.model.user_filter_mode_per_provider[provider] && this.model.user_filter_mode_per_provider[provider][index] || 'exclude';
        const exRows = this.model.excluded_users_per_provider && this.model.excluded_users_per_provider[provider];
        const excluded_users = userFilterMode === 'exclude' ? normalizeExcludedUsers(exRows && exRows[index]) : [];
        const inRows = this.model.included_users_per_provider && this.model.included_users_per_provider[provider];
        const included_users = userFilterMode === 'include' ? normalizeIncludedUsers(inRows && inRows[index]) : [];
        const followerFilter = this.model.follower_filter_per_provider && this.model.follower_filter_per_provider[provider] && this.model.follower_filter_per_provider[provider][index] || {};
        const follower_count_min = followerFilter.min;
        const follower_count_max = followerFilter.max;
        const follower_count_operator = followerFilter.operator;
        let row;

        const payload = {
          query,
          excluded_users,
          included_users,
          follower_count_min,
          follower_count_max,
          follower_count_operator
        };

        if (id !== null && id !== undefined) {
          const { data: res } = await axios.put(`${baseUrl}/${id}`, payload);
          row = res.data || res;
        } else {
          // Before creating a provider row, clean up any conflicting common rows
          const cIds = (this.configIds.common || []).filter(i => i !== null && i !== undefined);
          if (cIds.length > 0) {
            await this.deleteConfigIds(baseUrl, cIds);
            this.configIds.common = [];
          }
          payload.provider = provider;
          const { data: res } = await axios.post(baseUrl, payload);
          row = res.data || res;
        }

        this.$set(this.configIds[provider], index, row.id);
        this.$set(this.model.boolean_queries_per_provider[provider], index, row.query || query);
        if (Array.isArray(row.excluded_users)) {
          this.$set(this.model.excluded_users_per_provider[provider], index, normalizeExcludedUsers(row.excluded_users));
        } else {
          this.$set(this.model.excluded_users_per_provider[provider], index, excluded_users);
        }
        if (Array.isArray(row.included_users)) {
          this.$set(this.model.included_users_per_provider[provider], index, normalizeIncludedUsers(row.included_users));
        } else {
          this.$set(this.model.included_users_per_provider[provider], index, included_users);
        }
        this.$set(this.model.follower_filter_per_provider[provider], index, {
          min: row.follower_count_min,
          max: row.follower_count_max,
          operator: row.follower_count_operator
        });
        const hasIncluded = included_users.length > 0;
        
        // Ensure user_filter_mode_per_provider[provider] exists as an array
        if (!this.model.user_filter_mode_per_provider) {
          const modes = {};
          this.availableProviders.forEach(p => { modes[p] = []; });
          this.$set(this.model, 'user_filter_mode_per_provider', modes);
        }
        if (!Array.isArray(this.model.user_filter_mode_per_provider[provider])) {
          this.$set(this.model.user_filter_mode_per_provider, provider, []);
        }
        // Ensure the array is long enough
        while (this.model.user_filter_mode_per_provider[provider].length <= index) {
          this.model.user_filter_mode_per_provider[provider].push('exclude');
        }
        
        this.$set(this.model.user_filter_mode_per_provider[provider], index, hasIncluded ? 'include' : 'exclude');
        this.syncHasConfigFromConfigIds();
        this.snapshotState();
        this.notifySuccess(this.$t('istarshine.save_success'));
      } catch (error) {
        console.error('Error saving provider query:', error);
        this.errorMessage = error.response?.data?.message || this.$t('istarshine.save_error');
      } finally {
        this.savingRow = null;
      }
    },

    // ── Per-row Delete (removes from backend + UI) ──

    async removeCommonQuery(index) {
      const id = this.configIds.common[index];

      if (id !== null && id !== undefined) {
        const rowKey = 'common_' + index;
        this.deletingRow = rowKey;
        this.errorMessage = '';
        try {
          const baseUrl = `projects/${this.project_id}/${CONFIG_PATH}`;
          await axios.delete(`${baseUrl}/${id}`);
        } catch (error) {
          console.error('Error deleting common query:', error);
          this.errorMessage = error.response?.data?.message || this.$t('istarshine.delete_error');
          this.deletingRow = null;
          return;
        }
        this.deletingRow = null;
      }

      this.configIds.common.splice(index, 1);
      this.model.common_boolean_queries.splice(index, 1);
      if (Array.isArray(this.model.excluded_users_common)) {
        this.model.excluded_users_common.splice(index, 1);
      }
      if (Array.isArray(this.model.included_users_common)) {
        this.model.included_users_common.splice(index, 1);
      }
      if (Array.isArray(this.model.follower_filter_common)) {
        this.model.follower_filter_common.splice(index, 1);
      }
      if (Array.isArray(this.model.user_filter_mode_common)) {
        this.model.user_filter_mode_common.splice(index, 1);
      }

      if (this.model.common_boolean_queries.length === 0) {
        this.model.common_boolean_queries.push('');
        this.configIds.common.push(null);
        if (!Array.isArray(this.model.excluded_users_common)) {
          this.$set(this.model, 'excluded_users_common', [[]]);
        } else {
          this.model.excluded_users_common.push([]);
        }
        if (!Array.isArray(this.model.included_users_common)) {
          this.$set(this.model, 'included_users_common', [[]]);
        } else {
          this.model.included_users_common.push([]);
        }
        if (!Array.isArray(this.model.follower_filter_common)) {
          this.$set(this.model, 'follower_filter_common', [{ min: null, max: null, operator: null }]);
        } else {
          this.model.follower_filter_common.push({ min: null, max: null, operator: null });
        }
        if (!Array.isArray(this.model.user_filter_mode_common)) {
          this.$set(this.model, 'user_filter_mode_common', ['exclude']);
        } else {
          this.model.user_filter_mode_common.push('exclude');
        }
      }

      this.syncHasConfigFromConfigIds();
      this.snapshotState();
    },

    async removeProviderQuery(provider, index) {
      const ids = this.configIds[provider] || [];
      const id = ids[index];

      if (id !== null && id !== undefined) {
        const rowKey = provider + '_' + index;
        this.deletingRow = rowKey;
        this.errorMessage = '';
        try {
          const baseUrl = `projects/${this.project_id}/${CONFIG_PATH}`;
          await axios.delete(`${baseUrl}/${id}`);
        } catch (error) {
          console.error('Error deleting provider query:', error);
          this.errorMessage = error.response?.data?.message || this.$t('istarshine.delete_error');
          this.deletingRow = null;
          return;
        }
        this.deletingRow = null;
      }

      const arr = this.model.boolean_queries_per_provider[provider];
      if (Array.isArray(arr)) {
        arr.splice(index, 1);
        if (arr.length === 0) {
          arr.push('');
          this.configIds[provider].splice(index, 1);
          this.configIds[provider].push(null);
        } else {
          this.configIds[provider].splice(index, 1);
        }
      }
      const ex = this.model.excluded_users_per_provider && this.model.excluded_users_per_provider[provider];
      if (Array.isArray(ex)) {
        ex.splice(index, 1);
        if (ex.length === 0) {
          ex.push([]);
        }
      }
      const inc = this.model.included_users_per_provider && this.model.included_users_per_provider[provider];
      if (Array.isArray(inc)) {
        inc.splice(index, 1);
        if (inc.length === 0) {
          inc.push([]);
        }
      }
      const filt = this.model.follower_filter_per_provider && this.model.follower_filter_per_provider[provider];
      if (Array.isArray(filt)) {
        filt.splice(index, 1);
        if (filt.length === 0) {
          filt.push({ min: null, max: null, operator: null });
        }
      }
      const modes = this.model.user_filter_mode_per_provider && this.model.user_filter_mode_per_provider[provider];
      if (Array.isArray(modes)) {
        modes.splice(index, 1);
        if (modes.length === 0) {
          modes.push('exclude');
        }
      }

      this.syncHasConfigFromConfigIds();
      this.snapshotState();
    },

    // ── Delete All (bulk delete for a scope) ──

    async deleteConfigIds(baseUrl, ids) {
      for (const id of ids) {
        if (id !== null && id !== undefined) await axios.delete(`${baseUrl}/${id}`);
      }
    },

    async deleteSearch({ scope, provider }) {
      const baseUrl = `projects/${this.project_id}/${CONFIG_PATH}`;
      this.errorMessage = '';
      if (scope === 'common') {
        if (!this.configIds.common.some(id => id !== null && id !== undefined)) return;
        this.deletingCommon = true;
        try {
          await this.deleteConfigIds(baseUrl, this.configIds.common);
          this.configIds.common = [null];
          this.model.common_boolean_queries = [''];
          this.$set(this.model, 'excluded_users_common', [[]]);
          this.$set(this.model, 'included_users_common', [[]]);
          this.$set(this.model, 'follower_filter_common', [{ min: null, max: null, operator: null }]);
          this.$set(this.model, 'user_filter_mode_common', ['exclude']);
          this.syncHasConfigFromConfigIds();
          this.snapshotState();
          this.notifySuccess(this.$t('istarshine.delete_success'));
          if (!this.model.hasConfig) this.isEditMode = true;
        } catch (error) {
          console.error('Error deleting common config:', error);
          this.errorMessage = error.response?.data?.message || this.$t('istarshine.delete_error');
        } finally {
          this.deletingCommon = false;
        }
        return;
      }
      if (scope === 'provider' && provider && this.availableProviders.includes(provider)) {
        if (!(this.configIds[provider] || []).some(id => id !== null && id !== undefined)) return;
        this.deletingProvider = provider;
        try {
          await this.deleteConfigIds(baseUrl, this.configIds[provider]);
          this.$set(this.configIds, provider, [null]);
          this.$set(this.model.boolean_queries_per_provider, provider, ['']);
          this.$set(this.model.excluded_users_per_provider, provider, [[]]);
          this.$set(this.model.included_users_per_provider, provider, [[]]);
          this.$set(this.model.follower_filter_per_provider, provider, [{ min: null, max: null, operator: null }]);
          if (!this.model.user_filter_mode_per_provider) {
            this.$set(this.model, 'user_filter_mode_per_provider', {});
          }
          this.$set(this.model.user_filter_mode_per_provider, provider, ['exclude']);
          this.syncHasConfigFromConfigIds();
          this.snapshotState();
          this.notifySuccess(this.$t('istarshine.delete_success'));
          if (!this.model.hasConfig) this.isEditMode = true;
        } catch (error) {
          console.error('Error deleting provider config:', error);
          this.errorMessage = error.response?.data?.message || this.$t('istarshine.delete_error');
        } finally {
          this.deletingProvider = null;
        }
      }
    },

    // ── Query Type Switch (local only — no backend calls) ──

    onQueryTypeChanged() {
      this.clearValidation();
      this.$set(this.model, 'excluded_users_common', [[]]);
      this.$set(this.model, 'excluded_users_per_provider', emptyExcludedPerProvider(this.availableProviders));
      this.$set(this.model, 'included_users_common', [[]]);
      this.$set(this.model, 'included_users_per_provider', emptyIncludedPerProvider(this.availableProviders));
      this.$set(this.model, 'follower_filter_common', [{ min: null, max: null, operator: null }]);
      this.$set(this.model, 'follower_filter_per_provider', emptyFollowerFilterPerProvider(this.availableProviders));
      this.$set(this.model, 'user_filter_mode_common', ['exclude']);
      this.$set(this.model, 'user_filter_mode_per_provider', {});
    },

    clearValidation() {
      this.validationByField = {};
    },

    // ── Fetch ──

    fetchConfig() {
      this.isBusy = true;
      axios.get(`projects/${this.project_id}/${CONFIG_PATH}`)
        .then(({ data }) => {
          const list = (data && data.data && Array.isArray(data.data)) ? data.data : [];
          const configIds = emptyConfigIds(this.availableProviders);
          const emptyQueries = {};
          this.availableProviders.forEach(p => { emptyQueries[p] = ['']; });

          let query_type = 'all';
          let common_boolean_queries = [''];
          let excluded_users_common = [[]];
          let included_users_common = [[]];
          let follower_filter_common = [{ min: null, max: null, operator: null }];
          let user_filter_mode_common = ['exclude'];
          const boolean_queries_per_provider = { ...emptyQueries };
          const excluded_users_per_provider = emptyExcludedPerProvider(this.availableProviders);
          const included_users_per_provider = emptyIncludedPerProvider(this.availableProviders);
          const follower_filter_per_provider = emptyFollowerFilterPerProvider(this.availableProviders);
          const user_filter_mode_per_provider = {};

          const commonRows = [];
          const providerRows = {};
          this.availableProviders.forEach(p => { 
            providerRows[p] = []; 
            user_filter_mode_per_provider[p] = [];
          });

          list.forEach(item => {
            if (item.provider === null || item.provider === undefined) {
              commonRows.push(item);
            } else if (this.availableProviders.includes(item.provider)) {
              providerRows[item.provider].push(item);
            }
          });

          if (commonRows.length > 0) {
            query_type = 'all';
            configIds.common = commonRows.map(r => r.id);
            common_boolean_queries = commonRows.map(r => r.query || '');
            excluded_users_common = commonRows.map(r => normalizeExcludedUsers(r.excluded_users));
            included_users_common = commonRows.map(r => normalizeIncludedUsers(r.included_users));
            follower_filter_common = commonRows.map(r => ({
              min: r.follower_count_min,
              max: r.follower_count_max,
              operator: r.follower_count_operator
            }));
            user_filter_mode_common = commonRows.map(r => {
              const hasIncluded = Array.isArray(r.included_users) && r.included_users.length > 0;
              return hasIncluded ? 'include' : 'exclude';
            });
          }

          const hasPerProvider = this.availableProviders.some(p => providerRows[p].length > 0);
          if (hasPerProvider) {
            query_type = 'per_provider';
            this.availableProviders.forEach(p => {
              if (providerRows[p].length > 0) {
                configIds[p] = providerRows[p].map(r => r.id);
                boolean_queries_per_provider[p] = providerRows[p].map(r => r.query || '');
                excluded_users_per_provider[p] = providerRows[p].map(r => normalizeExcludedUsers(r.excluded_users));
                included_users_per_provider[p] = providerRows[p].map(r => normalizeIncludedUsers(r.included_users));
                follower_filter_per_provider[p] = providerRows[p].map(r => ({
                  min: r.follower_count_min,
                  max: r.follower_count_max,
                  operator: r.follower_count_operator
                }));
                user_filter_mode_per_provider[p] = providerRows[p].map(r => {
                  const hasIncluded = Array.isArray(r.included_users) && r.included_users.length > 0;
                  return hasIncluded ? 'include' : 'exclude';
                });
              }
            });
          }

          this.configIds = configIds;
          this.model = {
            query_type,
            common_boolean_queries,
            boolean_queries_per_provider: { ...boolean_queries_per_provider },
            excluded_users_common,
            excluded_users_per_provider,
            included_users_common,
            included_users_per_provider,
            follower_filter_common,
            follower_filter_per_provider,
            user_filter_mode_common,
            user_filter_mode_per_provider,
            hasConfig: list.length > 0
          };
          this.snapshotState();
          this.isEditMode = list.length === 0;
          this.isBusy = false;
        })
        .catch((error) => {
          console.error('Error fetching config:', error);
          const emptyQueries = {};
          this.availableProviders.forEach(p => { emptyQueries[p] = ['']; });
          this.model = {
            query_type: 'all',
            common_boolean_queries: [''],
            boolean_queries_per_provider: { ...emptyQueries },
            excluded_users_common: [[]],
            excluded_users_per_provider: emptyExcludedPerProvider(this.availableProviders),
            included_users_common: [[]],
            included_users_per_provider: emptyIncludedPerProvider(this.availableProviders),
            follower_filter_common: [{ min: null, max: null, operator: null }],
            follower_filter_per_provider: emptyFollowerFilterPerProvider(this.availableProviders),
            user_filter_mode_common: ['exclude'],
            user_filter_mode_per_provider: {},
            hasConfig: false
          };
          this.configIds = emptyConfigIds(this.availableProviders);
          this.snapshotState();
          this.isEditMode = true;
          this.isBusy = false;
        });
    },

    // ── Edit mode ──

    toggleEdit() {
      this.isEditMode = !this.isEditMode;
      this.errorMessage = '';
      if (!this.isEditMode) {
        this.model = cloneIstarshineModel(this.originalModel);
        this.configIds = cloneConfigIds(this.originalConfigIds);
      } else {
        this.snapshotState();
      }
    },

    cancelEdit() {
      this.isEditMode = false;
      this.model = cloneIstarshineModel(this.originalModel);
      this.configIds = cloneConfigIds(this.originalConfigIds);
      this.errorMessage = '';
    },

    notifySuccess(text) {
      this.$notify({
        group: 'toast_top_right',
        type: 'success',
        text: text || this.$t('istarshine.save_success')
      });
    }
  }
}
</script>
